import Card from "common/Boxes/Card";
import React, { memo, useContext, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import {
  BacktestingTimeframe,
  LanguageNameSpaces,
  MarketplaceSubGroup,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Flex from "common/Boxes/Flex";
import GroupTags from "features/newMarketplace/common/GroupTags";
import Text from "common/Typography/Text";
import { getNetPnlChange } from "@shared/features/newMarketplace/utils";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { typography } from "@shared/common/Themes/typography";
import PnlChart from "features/newMarketplace/common/PnlChart";
import RiskChart from "features/newMarketplace/common/RiskChart";
import GroupDollarRate from "features/newMarketplace/common/GroupDollarRate";
import FreeOrFreeTrialTag from "features/newMarketplace/common/FreeOrFreeTrialTag";
import {
  GROUP_CARD_HEIGHT,
  GROUP_CARD_WIDTH,
  SMALL_SCREEN_GROUP_CARD_HEIGHT,
} from "features/newMarketplace/common/constants";
import { buildMarketPlaceGroupEvent } from "utils/analytics";
import { getGroupExtraDetailsRoute } from "utils/routes";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { useHistory } from "react-router-dom";
import useMedia from "utils/hooks/useMedia";
import ChangeTag from "common/Tags/ChangeTag";
import GroupMainInfo from "features/newMarketplace/common/GroupMainInfo";
import {
  MOBILE_ONBOARDING_GROUP_CARD_HEIGHT,
  MOBILE_ONBOARDING_GROUP_CARD_WIDTH,
  ONBOARDING_GROUP_CARD_HEIGHT,
  ONBOARDING_GROUP_CARD_WIDTH,
} from "@shared/features/onboarding/constants";
import { MOBILE_VIRTUAL_LIST } from "common/constants";
import { GroupsPageContext } from "@shared/features/groups/GroupsPage.context";
import { GROUP_CARD_ID } from "testIds.ts";
import NewLookGroupCard from "features/newMarketplace/NewLook/NewLookGroupCard";

interface GroupCardProps {
  group: MarketplaceSubGroup;
  isTopTrader?: boolean;
  openScoreModal?: (group: MarketplaceSubGroup) => void;
  index: number;
  onCardClick?: () => void;
  isOnboarding?: boolean;
}

const GroupCard = ({
  group,
  isTopTrader,
  openScoreModal,
  index,
  onCardClick,
  isOnboarding,
}: GroupCardProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const isSmallScreen = useMedia([`(max-width: 1300px)`], [true], false);
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );
  const { isGroupsPage } = useContext(GroupsPageContext) || {};
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { analytics } = useContext(AnalyticsContext);
  const history = useHistory();

  const onGroupClick = (group: MarketplaceSubGroup, index: number) => {
    analytics?.marketplaceGroupClicked(
      buildMarketPlaceGroupEvent(group, index + 1)
    );
    history.push(
      getGroupExtraDetailsRoute(location.pathname, group.group_id.toString())
    );
  };

  const topSection = (
    <GroupMainInfo
      isOnboarding={isOnboarding}
      name={group.name}
      image={group.image_path}
      stars={group.avg_stars || 0}
      numReviewers={group.num_of_reviewers}
      numSubscribers={group.number_of_subscribers}
      scoreTagProps={{
        score: group.backtesting_score,
        isTopTrader,
        onClick: () => openScoreModal?.(group),
      }}
      groupId={group.group_id}
      groupData={group}
      hideCreateButton={true}
    />
  );

  const getPnlStatistic = (timeframe: BacktestingTimeframe) => {
    return (
      <StatisticWrapper>
        <StatisticTitle>
          {t("card.pnl", { months: timeframe / 30 })}
        </StatisticTitle>
        <ChangeTag
          value={getNetPnlChange(group, timeframe, displayCurrency)}
          suffix="%"
          style={{
            margin: "4px 0 0",
            fontSize: isSmallScreen
              ? typography.fontSizes.S
              : typography.fontSizes.M,
            lineHeight: "20px",
          }}
          arrowStyle={{ fontSize: isSmallScreen ? 8 : 9 }}
        />
      </StatisticWrapper>
    );
  };

  const riskChart = useMemo(() => {
    return <RiskChart scoreBreakdown={group.backtesting_score_breakdown} />;
  }, [group.backtesting_score_breakdown]);

  const statistics = (
    <Flex $justifyContent="center" style={{ width: "100%" }}>
      <Statistics
        $isMobile={isMobile}
        $isSmallScreen={isSmallScreen}
        $isOnboarding={isOnboarding}
      >
        {getPnlStatistic(180)}
        {getPnlStatistic(90)}
        {riskChart}
      </Statistics>
    </Flex>
  );

  const chart = useMemo(() => {
    return (
      <ChartWrapper>
        <PnlChart
          group={group}
          height={85}
          title={t("card.net_pnl_1m")}
          showChange={true}
        />
      </ChartWrapper>
    );
  }, [group, i18n.language]);

  const bottomSection = (
    <BottomSectionWrapper $isMobile={isMobile}>
      <GroupTags group={group} style={{ marginBottom: isMobile ? 12 : 0 }} />
      <Flex
        $justifyContent={isMobile ? "space-between" : "flex-end"}
        style={{ width: "100%" }}
      >
        <FreeOrFreeTrialTag
          hasFreeTrial={group.has_free_trial}
          isFree={group.is_free}
        />
        <GroupDollarRate
          priceNumber={group.price_number}
          style={{ marginLeft: 12 }}
        />
      </Flex>
    </BottomSectionWrapper>
  );

  const cardHeight = useMemo(() => {
    if (isOnboarding) {
      return isMobile
        ? MOBILE_ONBOARDING_GROUP_CARD_HEIGHT
        : ONBOARDING_GROUP_CARD_HEIGHT;
    } else if (isMobile) return "auto";
    else {
      return isSmallScreen ? SMALL_SCREEN_GROUP_CARD_HEIGHT : GROUP_CARD_HEIGHT;
    }
  }, [isOnboarding, isSmallScreen, isMobile]);

  const cardWidth = useMemo(() => {
    if (isGroupsPage) return "100%";
    else if (isOnboarding) {
      return isMobile
        ? MOBILE_ONBOARDING_GROUP_CARD_WIDTH
        : ONBOARDING_GROUP_CARD_WIDTH;
    } else {
      return isSmallScreen ? "100%" : GROUP_CARD_WIDTH;
    }
  }, [isGroupsPage, isOnboarding, isMobile, isSmallScreen]);

  return (
    <StyledCard
      onClick={() =>
        !!onCardClick ? onCardClick() : onGroupClick(group, index)
      }
      style={{ height: cardHeight, width: cardWidth }}
      data-testid={GROUP_CARD_ID}
    >
      {topSection}
      {statistics}
      {!isSmallScreen && !isOnboarding && chart}
      {(isSmallScreen || isOnboarding) && <Border />}
      {bottomSection}
    </StyledCard>
  );
};

const GroupCardSwitch = (props: GroupCardProps) => {
  const { isNewLook } = useTheme();
  if (isNewLook) return <NewLookGroupCard {...props} />;
  return <GroupCard {...props} />;
};

export default memo(GroupCardSwitch);

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
`;
const ChartWrapper = styled.div`
  margin: 12px 0;
  flex: auto;
`;
const Statistics = styled(Flex)<{
  $isMobile?: boolean;
  $isSmallScreen?: boolean;
  $isOnboarding?: boolean;
}>`
  margin-top: ${(p) => (p.$isMobile ? 12 : 24)}px;
  max-width: ${(p) => (p.$isOnboarding ? (p.$isMobile ? "100%" : 320) : 400)}px;
  max-height: 60px;
  width: 100%;
  text-align: center;
`;
const StatisticTitle = styled(Text).attrs({ type: "secondary" })`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
const StatisticWrapper = styled(Flex)`
  flex-direction: column;
`;
const Border = styled.div`
  border-top: ${(p) => p.theme.borders.light};
  padding-top: 12px;
  margin-top: 12px;
`;
const BottomSectionWrapper = styled(Flex)<{ $isMobile?: boolean }>`
  flex-direction: ${(p) => (p.$isMobile ? "column" : "row")};
  align-items: ${(p) => (p.$isMobile ? "flex-start" : "center")};
`;
